import store from 'store';

import { api } from '../api';

import type {
  UpdateBattlecardsType,
  CreateBattlecardPayloadType,
  BattlecardsType,
} from 'api/api.types';

const injectedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAllBattlecards: build.query<BattlecardsType[], void>({
      query: () => ({
        url: '/battlecards.json',
        method: 'GET',
      }),
      keepUnusedDataFor: 300, // 5 minutes (300 seconds)
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          // Populate the existing Rematch battlecards model
          store.dispatch.battlecards.setAllSortedByTitle(data);
        } catch {
          // Do nothing
        }
      },
      transformResponse: (response: any[]) => {
        // Map API data objects to BattlecardsType objects and sort by title (matching existing Rematch behavior)
        const battlecardsArray: BattlecardsType[] = response.map((bc: any) => {
          const {
            id,
            title,
            profile,
            cards,
            cardTitles,
            isDraft,
            layout,
            commentsCount,
            createdAt,
            updatedAt,
            review,
            viewOrder,
          } = bc;

          return {
            id,
            title: title || '',
            profile: profile?.id,
            profileName: profile?.name || '',
            cards: cards || { desktop: [], tablet: [], mobile: [] },
            cardTitles: Array.isArray(cardTitles)
              ? cardTitles.map((ct: any) =>
                  typeof ct === 'object' ? ct.id : ct,
                )
              : [],
            isDraft: isDraft || false,
            layout: layout || 'stacker',
            commentsCount: commentsCount || 0,
            createdAt: createdAt || '',
            updatedAt: updatedAt || '',
            viewOrder: viewOrder || '',
            review: review || { reviewedAt: null, byUserId: null },
          };
        });

        // Sort by title (case-insensitive)
        return battlecardsArray.sort((a, b) => a.title.localeCompare(b.title));
      },
    }),
    updateBattlecard: build.mutation<any, UpdateBattlecardsType>({
      query: (battlecard) => {
        const { id, ...body } = battlecard;
        return {
          url: `/battlecards/${id}.json`,
          method: 'PUT',
          body,
          headers: {
            'Content-Type': 'application/json',
          },
        };
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const {
            data: {
              cards,
              cardTitles,
              commentsCount,
              createdAt,
              id,
              layout,
              isDraft,
              profile,
              review,
              title,
              updatedAt,
              viewOrder,
            },
          } = await queryFulfilled;
          store.dispatch.battlecards.update({
            id,
            ...(cards && { cards }),
            ...(cardTitles && { cardTitles }),
            ...(typeof commentsCount === 'number' && { commentsCount }),
            ...(createdAt && { createdAt }),
            ...(typeof isDraft === 'boolean' && { isDraft }),
            ...(layout && { layout }),
            ...(review && { review }),
            ...(title && { title }),
            ...(updatedAt && { updatedAt }),
            ...(typeof viewOrder === 'string' && { viewOrder }),
            ...(profile?.id && { profile: profile.id }),
          });
        } catch {
          // Do nothing
        }
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
    createBattlecard: build.mutation<any, Partial<CreateBattlecardPayloadType>>(
      {
        query: (payload) => {
          const { profileId, ...body } = payload;

          return {
            url: `/profiles/${profileId}/battlecards.json`,
            method: 'POST',
            body,
            headers: {
              'Content-Type': 'application/json',
            },
          };
        },
        transformResponse: (response: any) => {
          return response;
        },
      },
    ),
    deleteBattlecard: build.mutation<void, number>({
      query: (battlecardId) => ({
        url: `/battlecards/${battlecardId}.json`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      onQueryStarted: async (battlecardId, { queryFulfilled }) => {
        try {
          await queryFulfilled;
          store.dispatch.battlecards.delete(battlecardId);
        } catch {
          // Do nothing
        }
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
  }),
});

export const enhancedApi = injectedApi.enhanceEndpoints({
  addTagTypes: ['Battlecards'],
});

export const {
  useGetAllBattlecardsQuery,
  useUpdateBattlecardMutation,
  useCreateBattlecardMutation,
  useDeleteBattlecardMutation,
} = enhancedApi;
